import { notificationsApi } from './api';
import { urlBase64ToUint8Array } from './helpers';

/**
 * Request permission for notifications
 * @returns {Promise<string>} - The permission state (granted, denied, default)
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'denied';
  }
  
  return await Notification.requestPermission();
}

/**
 * Subscribe to push notifications
 * @returns {Promise<PushSubscription|null>} - The push subscription or null if it fails
 */
export async function subscribeToPushNotifications() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.error('Push notifications are not supported in this browser');
      return null;
    }
    
    // Get registration from service worker
    const registration = await navigator.serviceWorker.ready;
    
    // Get the server's public key
    const { publicKey } = await notificationsApi.getVapidPublicKey();
    
    // Convert the public key to a Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(publicKey);
    
    // Subscribe the user
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    
    // Send the subscription to the server
    await notificationsApi.saveSubscription(subscription);
    
    console.log('Push notification subscription successful');
    return subscription;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return null;
  }
}

/**
 * Check if the browser supports push notifications
 * @returns {boolean} - True if notifications are supported
 */
export function isPushNotificationSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Get the current push notification subscription
 * @returns {Promise<PushSubscription|null>} - The current subscription or null
 */
export async function getCurrentPushSubscription() {
  if (!isPushNotificationSupported()) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('Error getting push subscription:', error);
    return null;
  }
}

/**
 * Check if the current browser is subscribed to push notifications on the server
 * @returns {Promise<boolean>} - True if the browser is subscribed
 */
export async function isCurrentBrowserSubscribed() {
  try {
    // First check if browser supports push notifications
    if (!isPushNotificationSupported()) {
      return false;
    }
    
    // Get the current subscription
    const subscription = await getCurrentPushSubscription();
    
    // If no subscription, the browser is not subscribed
    if (!subscription) {
      return false;
    }
    
    // Check with the server if this endpoint is subscribed
    const response = await notificationsApi.isSubscribed(subscription.endpoint);
    
    // Return true if the server confirms the subscription
    return response.success && response.data === true;
  } catch (error) {
    console.error('Error checking if browser is subscribed:', error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<boolean>} - True if unsubscription was successful
 */
export async function unsubscribeFromPushNotifications() {
  try {
    const subscription = await getCurrentPushSubscription();
    if (!subscription) {
      return true;
    }
    
    const result = await subscription.unsubscribe();
    
    if (result) {
      // Notify the server about unsubscription
      await notificationsApi.deleteSubscription(subscription.endpoint);
    }
    
    return result;
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return false;
  }
} 